import { gql, type TypedDocumentNode } from '@apollo/client';
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import type { BonusOrdinalRatingEnum } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { CollectionArrow } from '@sorare/core/src/atoms/icons/CollectionArrow';
import { LineupBonuses } from '@sorare/core/src/atoms/icons/LineupBonuses';
import { iconArrowDownCircle } from '@sorare/core/src/atoms/icons/system/iconArrowDownCircle.ts';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandXXS, LabelL } from '@sorare/core/src/atoms/typography';
import { InGameCurrencyBalance } from '@sorare/core/src/components/balance/InGameCurrencyBalance';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql/index.ts';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';

import type { ComposeBonuses_so5AppearanceBonusInterface } from '#src/components/leaderboard/ComposeBonuses/__generated__/index.graphql.ts';
import { ComposeBonuses } from '#src/components/leaderboard/ComposeBonuses/index.tsx';
import { bonusRatingMessages, colorPerBonusRating } from '#src/lib/bonus.ts';

import { AppearanceAvatar } from './AppearanceAvatar/index.tsx';
import { AppearanceBonusBreakdown } from './AppearanceBonusBreakdown/index.tsx';
import { SetAppearanceBonusBreakdown } from './SetAppearanceBonusBreakdown/index.tsx';
import type {
  LineupBonusDetails_appearanceInterface,
  LineupBonusDetails_so5Appearance,
  LineupBonusDetails_so5AppearanceBonusInterface,
} from './__generated__/index.graphql.ts';

const Root = styled.div`
  height: 100%;
  flex: 1;
  display: grid;
  grid-template-rows: auto min-content;
`;
const Content = styled(FullWidthContainer)`
  overflow: auto;
  padding-bottom: var(--double-unit);
  display: grid;
  grid-template-rows: min-content auto;
`;
const Footer = styled(FullWidthContainer)`
  border-top: 1px solid var(--c-nd-150);
  padding-top: var(--double-unit);
  padding-bottom: var(--triple-unit);
  overflow: hidden;
  max-width: 100%;
`;
const AppearanceWrapper = styled(Vertical)`
  gap: var(--unit);
`;
const Grid = styled.div`
  display: grid;
  padding: var(--unit);
  grid-template-columns: repeat(var(--nb-appearances), 65px);
  gap: var(--intermediate-unit);
  overflow-x: auto;
  max-width: 100%;
  width: 100%;

  &:has(${AppearanceWrapper}:nth-child(7)) {
    gap: 0;
  }
`;
const FullHeightWrapper = styled.div`
  flex: 1;
`;

type Props = {
  bonuses: ComposeBonuses_so5AppearanceBonusInterface[];
  bonusRating?: BonusOrdinalRatingEnum | null;
  bonusPoints?: number | null;
  appearances:
    | LineupBonusDetails_appearanceInterface[]
    | LineupBonusDetails_so5Appearance[];
  initiallySelectedAppearanceIndex?: number;
  showXpBalance?: boolean;
  isSet?: boolean;
};

export const LineupBonusDetails = ({
  bonuses,
  bonusRating,
  bonusPoints,
  appearances,
  initiallySelectedAppearanceIndex,
  showXpBalance = true,
  isSet = false,
}: Props) => {
  const {
    so5: { inSeasonName, cardMaxGrade },
  } = useConfigContext();
  const { currentUser } = useCurrentUserContext();
  const { xpBalances = [] } = currentUser || {};

  const [selectedAppearanceIndex, setSelectedAppearanceIndex] = useState<
    number | undefined
  >(initiallySelectedAppearanceIndex);

  const bonusRatingColor = bonusRating
    ? colorPerBonusRating[bonusRating]
    : undefined;

  const selectedAppearance =
    typeof selectedAppearanceIndex === 'number'
      ? appearances.at(selectedAppearanceIndex)
      : null;

  const xpBalance = xpBalances.find(
    ({ currency }) =>
      inGameCurrencyToRarity[currency] === selectedAppearance?.rarity
  );

  return (
    <Root>
      <Content>
        <SBHorizontal>
          {!!xpBalance && !!showXpBalance && (
            <InGameCurrencyBalance
              currency={xpBalance.currency}
              amount={xpBalance.amount}
            />
          )}
        </SBHorizontal>
        {selectedAppearance ? (
          isSet ? (
            <SetAppearanceBonusBreakdown
              key={selectedAppearance.id}
              appearance={selectedAppearance}
            />
          ) : (
            <AppearanceBonusBreakdown
              key={selectedAppearance.id}
              appearance={selectedAppearance}
              cardMaxGrade={cardMaxGrade || 0}
              seasonalityName={inSeasonName}
            />
          )
        ) : (
          <Vertical>
            <FullHeightWrapper>
              <ComposeBonuses
                customTitle={
                  <Horizontal>
                    <LineupBonuses size={2} />
                    <LabelL>
                      <FormattedMessage defaultMessage="Lineup Bonuses" />
                    </LabelL>
                  </Horizontal>
                }
                lineupBonuses={bonuses}
                inline
              />
            </FullHeightWrapper>
            <Vertical gap={0.5}>
              <BodyM color="var(--c-blue-300)" className="text-center">
                <FormattedMessage defaultMessage="Pick a player to view their bonus" />
              </BodyM>
              <FontAwesomeIcon
                color="var(--c-blue-300)"
                icon={iconArrowDownCircle}
              />
            </Vertical>
          </Vertical>
        )}
      </Content>
      <Footer>
        <Vertical gap={1.5}>
          {!!bonusRating && (
            <Horizontal>
              <CollectionArrow color={bonusRatingColor} />
              <LabelL bold>
                <Horizontal>
                  {typeof bonusPoints === 'number' ? (
                    <FormattedMessage
                      defaultMessage="Overall Card Bonuses: <span>{rating}</span>"
                      values={{
                        span: (...chunks: ReactNode[]) => (
                          <Horizontal
                            gap={0.5}
                            style={{
                              color: bonusRatingColor,
                            }}
                          >
                            {chunks}
                          </Horizontal>
                        ),
                        rating: (
                          <FormattedMessage
                            {...fantasy.ordinalPoints}
                            values={{
                              score: (
                                <FormattedNumber
                                  value={bonusPoints || 0}
                                  maximumFractionDigits={1}
                                  signDisplay="always"
                                />
                              ),
                              sup: (...chunks: ReactNode[]) => (
                                <BrandXXS bold>{chunks}</BrandXXS>
                              ),
                            }}
                          />
                        ),
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      defaultMessage="Team Bonus Rating: <span>{rating}</span>"
                      values={{
                        span: (...chunks: ReactNode[]) => (
                          <span
                            style={{
                              color: bonusRatingColor,
                            }}
                          >
                            {chunks}
                          </span>
                        ),
                        rating: (
                          <FormattedMessage
                            {...bonusRatingMessages[bonusRating]}
                          />
                        ),
                      }}
                    />
                  )}
                </Horizontal>
              </LabelL>
            </Horizontal>
          )}
          <Grid
            style={
              {
                '--nb-appearances': appearances.length,
              } as CSSProperties
            }
          >
            {appearances.map((appearance, i) => {
              const appearanceBonusRating = isType(appearance, 'So5Appearance')
                ? appearance.bonusRating
                : null;

              return (
                <AppearanceWrapper key={appearance.id}>
                  <AppearanceAvatar
                    appearance={appearance}
                    bonusRating={appearanceBonusRating}
                    selected={selectedAppearanceIndex === i}
                    onClick={() => {
                      setSelectedAppearanceIndex(i);
                    }}
                  />
                </AppearanceWrapper>
              );
            })}
          </Grid>
        </Vertical>
      </Footer>
    </Root>
  );
};

LineupBonusDetails.fragments = {
  appearanceInterface: gql`
    fragment LineupBonusDetails_appearanceInterface on AppearanceInterface {
      id
      rarity
      ...AppearanceBonusBreakdown_appearance
      ...AppearanceAvatar_appearanceInterface
      ...SetAppearanceBonusBreakdown_appearance
      ... on So5Appearance {
        id
        bonusRating
      }
    }
    ${AppearanceAvatar.fragments.appearanceInterface}
    ${AppearanceBonusBreakdown.fragments.appearance}
    ${SetAppearanceBonusBreakdown.fragments.appearance}
  ` as TypedDocumentNode<LineupBonusDetails_appearanceInterface>,
  so5Appearance: gql`
    fragment LineupBonusDetails_so5Appearance on So5Appearance {
      id
      rarity
      bonusRating
      ...AppearanceBonusBreakdown_appearance
      ...AppearanceAvatar_appearanceInterface
      ...SetAppearanceBonusBreakdown_appearance
    }
    ${AppearanceAvatar.fragments.appearanceInterface}
    ${AppearanceBonusBreakdown.fragments.appearance}
    ${SetAppearanceBonusBreakdown.fragments.appearance}
  ` as TypedDocumentNode<LineupBonusDetails_so5Appearance>,
  so5AppearanceBonusInterface: gql`
    fragment LineupBonusDetails_so5AppearanceBonusInterface on So5AppearanceBonusInterface {
      ...ComposeBonuses_so5AppearanceBonusInterface
    }
    ${ComposeBonuses.fragments.so5AppearanceBonusInterface}
  ` as TypedDocumentNode<LineupBonusDetails_so5AppearanceBonusInterface>,
};
